import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CATS = ["Food & Catering", "Services", "Healthcare", "Fashion", "Vehicles", "Housing", "Business & Pro", "Events", "Other"];

export default function CreateBusiness() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", category: CATS[0], tagline: "", description: "", location: "",
    address: "", website: "", phone: "", email: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (!loading && !user) nav("/login"); }, [loading, user, nav]);
  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.description.trim() || !form.location.trim()) return toast.error("Name, location, and description are required");
    setSaving(true);
    try {
      const { data } = await api.post("/businesses", form);
      toast.success("Business page created");
      nav(`/business/${data.slug}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">New business page</div>
      <h1 className="font-display text-4xl text-[#1E3A2F] mt-1">Claim your permanent page</h1>
      <p className="text-sm text-[#5C5C5C] mt-2">Every recommendation the community writes for you will collect here — trust that lasts.</p>

      <form onSubmit={submit} className="mt-8 space-y-6 bg-white border border-[#E2DED0] rounded-2xl p-6 sm:p-8">
        <div>
          <Label>Business name</Label>
          <Input data-testid="biz-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nairobi Kitchen Catering" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="biz-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Location</Label>
            <Input data-testid="biz-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Tacoma, WA" />
          </div>
        </div>
        <div>
          <Label>One-line tagline</Label>
          <Input data-testid="biz-tagline" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Kenyan flavors for every occasion" />
        </div>
        <div>
          <Label>About</Label>
          <Textarea data-testid="biz-description" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What you do, who you serve, what makes you great." />
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>Phone</Label>
            <Input data-testid="biz-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="biz-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <Label>Website</Label>
            <Input data-testid="biz-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" />
          </div>
        </div>
        <div>
          <Label>Address (optional)</Label>
          <Input data-testid="biz-address" value={form.address} onChange={(e) => set("address", e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={() => nav(-1)}>Cancel</Button>
          <Button data-testid="biz-submit" type="submit" disabled={saving} className="abm-btn-primary rounded-full px-6">{saving ? "Saving…" : "Publish page"}</Button>
        </div>
      </form>
    </div>
  );
}
